import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productService } from '../../services/productService';
import ProductForm from '../../components/Products/ProductForm';
import Toast from '../../components/Common/Toast';
import { useCanMutate } from '../../hooks/useCanMutate';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';

const EditProduct = () => {
  const canMutate = useCanMutate();
  const { produccionEnabled, ventasEnabled } = useModuleSettings();
  const recipeVendibleEditable = Boolean(produccionEnabled && ventasEnabled);
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const response = await productService.getProduct(id);

        if (response.success && response.data) {
          setProduct({ ...response.data, id: parseInt(id, 10) });
        } else {
          setToast({
            isOpen: true,
            message: 'Producto no encontrado',
            type: 'error',
          });
          setTimeout(() => {
            navigate('/products');
          }, 2000);
        }
      } catch (error) {
        setToast({
          isOpen: true,
          message: error.response?.data?.message || 'Error al cargar el producto',
          type: 'error',
        });
        setTimeout(() => {
          navigate('/products');
        }, 2000);
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, navigate]);

  const handleSuccess = () => {
    setToast({
      isOpen: true,
      message: 'Producto actualizado exitosamente',
      type: 'success',
    });
    setTimeout(() => {
      navigate('/products');
    }, 1500);
  };

  const handleCancel = () => {
    navigate('/products');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center py-12">
            <div className="text-gray-500">Cargando...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  if (product.linked_to_recipe && !recipeVendibleEditable) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto">
          <div className="rounded-lg border border-violet-200 bg-violet-50 px-5 py-6 shadow-sm">
            <h1 className="text-xl font-semibold text-gray-900">Este producto no se edita aquí</h1>
            <p className="mt-2 text-sm text-gray-700 leading-relaxed">
              <span className="font-mono text-teal-700">{product.sku}</span>
              {' · '}
              <strong>{product.name}</strong>
              {' '}es el producto terminado vinculado a una receta elaborada. Para cambiar nombre, unidad u otros datos
              asociados a la receta, use <strong>Producción</strong>, <strong>Producción concesionaria</strong> o{' '}
              <strong>Restaurantes</strong> → clasificación de recetas / recetas.
            </p>
            <p className="mt-3 text-sm text-gray-600">
              Para marcarlo como vendible en Ventas, active los módulos <strong>Producción</strong> y{' '}
              <strong>Ventas</strong>.
            </p>
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="mt-5 px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors"
            >
              Volver a productos
            </button>
          </div>
          <Toast
            isOpen={toast.isOpen}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            {product.linked_to_recipe
              ? (canMutate ? 'Producto de receta — vendible' : 'Ver producto de receta')
              : (canMutate ? 'Editar Producto' : 'Ver Producto')}
          </h1>
          {canMutate && (
            <p className="text-gray-600 mt-1">
              {product.linked_to_recipe
                ? 'Solo puede marcar si el producto es vendible en Ventas. Nombre, unidad y demás datos se gestionan en la receta.'
                : 'Modifique la información del producto'}
            </p>
          )}
          <div className="mt-2">
            <span className="text-sm font-mono text-teal-600">SKU: {product.sku}</span>
          </div>
        </div>

        <ProductForm
          isOpen={true}
          onClose={handleCancel}
          product={product}
          onSuccess={handleSuccess}
          isPageMode={true}
          recipeLinkedMode={Boolean(product.linked_to_recipe)}
        />

        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, isOpen: false })}
        />
      </div>
    </div>
  );
};

export default EditProduct;
